// pages/ManageUsers.tsx — FINAL RTP FULLY INTEGRATED VERSION
// Real-time sync, optimistic actions, RTP warnings, toast feedback, color-coded status

import { useState, useEffect } from 'react';
import { ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import DOMPurify from 'dompurify';
import useAdmin from '../hooks/useAdmin';
import { useConfirmModal } from '../utils/modal';
import ConfirmModal from '../../shared/components/ConfirmModal';
import { toast } from 'react-hot-toast'; // npm install react-hot-toast

interface User {
  id: string;
  login_id?: string;
  email?: string;
  name: string;
  phone?: string;
  manager_email?: string;
  real_credits: number;
  mode: 'cash';
  banned: boolean;
  suspended: boolean;
}

interface SearchForm {
  search: string;
}

export default function ManageUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof User>('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [loading, setLoading] = useState(false);
  const { token, fetchUsers, banUser, suspendUser, deleteUser, error, setError, socket } = useAdmin();
  const { isOpen, message, showConfirmModal, handleConfirm, handleCancel } = useConfirmModal();
  const { register, watch } = useForm<SearchForm>({ defaultValues: { search: '' } });
  const search = watch('search');

  useEffect(() => {
    if (token) {
      setLoading(true);
      fetchUsers()
        .then(data => setUsers((data || []) as any[]))
        .finally(() => setLoading(false));
    }
  }, [token, fetchUsers]);

  useEffect(() => {
    const handleRefresh = () => {
      setLoading(true);
      fetchUsers()
        .then(data => setUsers((data || []) as any[]))
        .finally(() => setLoading(false));
    };
    window.addEventListener('refresh-users', handleRefresh);
    return () => window.removeEventListener('refresh-users', handleRefresh);
  }, [fetchUsers]);

  // Real-time sync for users + RTP-related events
  useEffect(() => {
    if (!socket) return;

    const events = [
      'credits-updated',
      'user-banned',
      'user-unbanned',
      'user-suspended',
      'user-unsuspended',
      'user-updated',
      'rtp-updated',          // RTP change affects users indirectly
      'jackpot-win'           // Jackpot may affect credits
    ];

    events.forEach(e => {
      socket.on(e, () => {
        if (['credits-updated', 'user-updated'].includes(e)) {
          toast('User data updated in real-time');
        }
        fetchUsers().then(data => setUsers((data || []) as any[]));
      });
    });

    return () => events.forEach(e => socket.off(e));
  }, [socket, fetchUsers]);

  const handleSort = (column: keyof User) => {
    setSortAsc(sortColumn === column ? !sortAsc : true);
    setSortColumn(column);
  };

  const sortedUsers = [...users].sort((a, b) => {
    const aVal = a[sortColumn] ?? '';
    const bVal = b[sortColumn] ?? '';
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortAsc ? aVal - bVal : bVal - aVal;
    }
    return sortAsc 
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  const filtered = sortedUsers.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    (u.login_id || u.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleBan = (user: User) => {
    const action = user.banned ? 'unban' : 'ban';
    showConfirmModal(
      `Are you sure you want to ${action} user ${user.login_id || user.name}? ${action === 'ban' ? 'This will block RTP participation.' : 'This will restore RTP access.'}`,
      async () => {
        try {
          // Optimistic update
          setUsers(prev =>
            prev.map(u => u.id === user.id ? { ...u, banned: !user.banned } : u)
          );

          await banUser(user.id, !user.banned);
          toast.success(`User ${action}ned: ${user.login_id || user.name}`);
        } catch (err: any) {
          // Revert
          setUsers(prev =>
            prev.map(u => u.id === user.id ? { ...u, banned: user.banned } : u)
          );
          setError(`Failed to ${action}: ${err.message}`);
          toast.error(`Failed to ${action}: ${err.message}`);
        }
      }
    );
  };

  const handleSuspend = (user: User) => {
    const action = user.suspended ? 'unsuspend' : 'suspend';
    showConfirmModal(
      `Are you sure you want to ${action} user ${user.login_id || user.name}? ${action === 'suspend' ? 'This will pause RTP activity.' : 'This will resume RTP activity.'}`,
      async () => {
        try {
          // Optimistic
          setUsers(prev =>
            prev.map(u => u.id === user.id ? { ...u, suspended: !user.suspended } : u)
          );

          await suspendUser(user.id, !user.suspended);
          toast.success(`User ${action}ed: ${user.login_id || user.name}`);
        } catch (err: any) {
          // Revert
          setUsers(prev =>
            prev.map(u => u.id === user.id ? { ...u, suspended: user.suspended } : u)
          );
          setError(`Failed to ${action}: ${err.message}`);
          toast.error(`Failed to ${action}: ${err.message}`);
        }
      }
    );
  };

  const handleDelete = (user: User) => {
    showConfirmModal(
      `Delete user ${user.login_id || user.name} permanently? This will revoke all RTP participation and cannot be undone.`,
      async () => {
        try {
          // Optimistic remove
          setUsers(prev => prev.filter(u => u.id !== user.id));

          await deleteUser(user.id);
          toast.success(`User deleted: ${user.login_id || user.name}`);
        } catch (err: any) {
          // Revert
          setUsers(prev => [...prev, user]);
          setError(`Delete failed: ${err.message}`);
          toast.error(`Delete failed: ${err.message}`);
        }
      }
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-900">Manage Users</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-700 hover:text-red-900 font-bold">
            ✕
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <input
            type="text"
            placeholder="Search by name or login ID..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            {...register('search')}
            aria-label="Search users"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-indigo-600" size={48} />
            <span className="ml-4 text-xl text-gray-700">Loading users...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-gray-500 py-20">
            No users found {search ? `matching "${search}"` : ''}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                <tr>
                  {[
                    { key: 'name', label: 'Name' },
                    { key: 'email', label: 'Email' },
                    { key: 'phone', label: 'Phone' },
                    { key: 'manager_email', label: 'Manager' },
                    { key: 'real_credits', label: 'Real Credits' },
                    { key: 'mode', label: 'Mode' },
                    { key: 'banned', label: 'Banned' },
                    { key: 'suspended', label: 'Suspended' },
                  ].map(({ key, label }) => (
                    <th
                      key={key}
                      className="p-4 cursor-pointer text-left hover:bg-indigo-700 transition"
                      onClick={() => handleSort(key as keyof User)}
                    >
                      <div className="flex items-center gap-1">
                        {label}
                        {sortColumn === key && (sortAsc ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                      </div>
                    </th>
                  ))}
                  <th className="p-4 text-left font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id || u.login_id} className="border-b hover:bg-gray-50 transition">
                    <td className="p-4 font-medium" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(u.name || 'N/A') }} />
                    <td className="p-4 text-gray-700" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(u.login_id || u.email || '—') }} />
                    <td className="p-4 text-gray-600" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(u.phone || 'N/A') }} />
                    <td className="p-4 text-gray-600">{u.manager_email || 'None'}</td>
                    <td className="p-4 text-center font-medium text-green-700">{Number(u.real_credits).toFixed(2)}</td>
                    <td className="p-4 text-center">
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Cash
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        u.banned ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {u.banned ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        u.suspended ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {u.suspended ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="p-4 space-x-2 flex flex-wrap gap-2">
                      <button
                        className={`${u.banned ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white px-3 py-1.5 rounded-lg transition shadow-sm text-sm`}
                        onClick={() => handleBan(u)}
                      >
                        {u.banned ? 'Unban' : 'Ban'}
                      </button>
                      <button
                        className={`${u.suspended ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-600 hover:bg-yellow-700'} text-white px-3 py-1.5 rounded-lg transition shadow-sm text-sm`}
                        onClick={() => handleSuspend(u)}
                      >
                        {u.suspended ? 'Unsuspend' : 'Suspend'}
                      </button>
                      <a
                        href={`/admin/dashboard?section=edit-user&id=${u.id}`}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition shadow-sm text-sm"
                      >
                        Edit
                      </a>
                      <button
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg transition shadow-sm text-sm"
                        onClick={() => handleDelete(u)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={isOpen}
        message={message}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}
